import { useState, useMemo } from 'react';
import { calculateOEE, type OEEResult } from '@/utils/oeeCalculator';
import { toInputDate } from '@/utils/format';
import SearchableSelect from '@/components/base/SearchableSelect';
import { useWorkCenters } from '@/hooks/useWorkCenters';

interface OEEDashboardProps {
  machines?: any[];
  workCenters?: any[];
  shifts?: any[];
  productionEntries?: any[];
  downtimeEntries?: any[];
}

function barColor(pct: number): string {
  if (pct >= 85) return 'bg-emerald-500';
  if (pct >= 60) return 'bg-amber-500';
  return 'bg-rose-500';
}

function textColor(pct: number): string {
  if (pct >= 85) return 'text-emerald-700';
  if (pct >= 60) return 'text-amber-700';
  return 'text-rose-700';
}

function ratingLabel(pct: number): string {
  if (pct >= 85) return 'World class';
  if (pct >= 60) return 'Good';
  return 'Needs improvement';
}

function statusDot(status: string): string {
  switch (status) {
    case 'RUNNING':
      return 'bg-emerald-500';
    case 'MAINTENANCE':
      return 'bg-amber-500';
    case 'BREAKDOWN':
      return 'bg-rose-500';
    default:
      return 'bg-slate-400';
  }
}

function formatQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function shortDay(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** Percentage cell with a thin bar under the number */
function Metric({ pct, muted }: { pct: number; muted?: boolean }) {
  if (muted) return <span className="text-[#94a3b8]">—</span>;
  return (
    <div className="min-w-[64px]">
      <div className={`text-xs font-semibold ${textColor(pct)}`}>{pct}%</div>
      <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden mt-0.5">
        <div className={`h-full rounded-full ${barColor(pct)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function OEEDashboard(props: OEEDashboardProps) {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const machines = props.machines || [];
  const workCenters = props.workCenters || [];
  const shifts = props.shifts || [];
  const productionEntries = props.productionEntries || [];
  const downtimeEntries = props.downtimeEntries || [];

  const [selectedDate, setSelectedDate] = useState(() => toInputDate(new Date()));
  const [selectedShift, setSelectedShift] = useState('all');
  const [workCenterFilter, setWorkCenterFilter] = useState('all');

  const oeeData = useMemo(
    () => ({ shifts, productionEntries, downtimeEntries }),
    [shifts, productionEntries, downtimeEntries],
  );

  const last7Days = useMemo(() => {
    const base = new Date(`${selectedDate}T00:00:00`);
    const days: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(base);
      d.setDate(d.getDate() - i);
      days.push(toInputDate(d));
    }
    return days;
  }, [selectedDate]);

  const rows = useMemo(() => {
    return machines
      .filter((m) => workCenterFilter === 'all' || m.workCenterId === workCenterFilter)
      .map((m) => {
        const wc = workCenters.find((w) => w.id === m.workCenterId);
        return {
          machine: m,
          workCenterName: wc?.name || m.workCenterName || '—',
          oee: calculateOEE(m, selectedDate, selectedShift, oeeData),
          trend: last7Days.map((day) => calculateOEE(m, day, selectedShift, oeeData)),
        };
      })
      .sort((a, b) => Number(b.oee.hasData) - Number(a.oee.hasData) || b.oee.oee - a.oee.oee);
  }, [machines, workCenters, workCenterFilter, selectedDate, selectedShift, oeeData, last7Days]);

  // Plant figures: totals across the machines that had planned time and activity
  const plant = useMemo(() => {
    const measured = rows.filter((r) => r.oee.hasData);
    const avg = (pick: (o: OEEResult) => number) =>
      measured.length > 0 ? Math.round(measured.reduce((s, r) => s + pick(r.oee), 0) / measured.length) : null;
    return {
      measured: measured.length,
      oee: avg((o) => o.asPercent.oee),
      availability: avg((o) => o.asPercent.availability),
      performance: avg((o) => o.asPercent.performance),
      quality: avg((o) => o.asPercent.quality),
      produced: rows.reduce((s, r) => s + r.oee.producedQty, 0),
      rejected: rows.reduce((s, r) => s + r.oee.rejectedQty, 0),
      downtime: rows.reduce((s, r) => s + r.oee.downtimeMinutes, 0),
    };
  }, [rows]);

  const plannedShiftCount = useMemo(
    () => rows.reduce((max, r) => Math.max(max, r.oee.plannedMinutes), 0),
    [rows],
  );

  const cards = [
    { label: 'OEE', value: plant.oee, note: plant.oee === null ? '' : ratingLabel(plant.oee) },
    { label: 'Availability', value: plant.availability, note: 'run time / planned time' },
    { label: 'Performance', value: plant.performance, note: 'output / machine capacity' },
    { label: 'Quality', value: plant.quality, note: 'good units / total units' },
  ];

  return (
    <div className="space-y-3">
      {/* Controls + plant summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col xl:flex-row xl:items-center gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <input
            type="date"
            value={selectedDate}
            max={toInputDate(new Date())}
            onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
            className="h-9 text-sm border border-slate-200 rounded-lg px-2.5 text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
          <div className="w-44">
            <SearchableSelect
              value={selectedShift}
              onChange={(val) => setSelectedShift(val || 'all')}
              options={[{ value: 'all', label: 'All Shifts' }, ...shifts.map((s) => ({ value: s.id, label: s.name }))]}
              placeholder="All Shifts"
            />
          </div>
          <div className="w-48">
            <SearchableSelect
              value={workCenterFilter}
              onChange={(val) => setWorkCenterFilter(val || 'all')}
              options={[{ value: 'all', label: 'All Work Centers' }, ...workCenters.map((w) => ({ value: w.id, label: w.name }))]}
              placeholder="All Work Centers"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 flex-1">
          {cards.map((c) => (
            <div key={c.label} className="border border-slate-200 rounded-lg px-3 py-2">
              <div className="text-[11px] font-medium text-[#64748b]">{c.label}</div>
              <div className={`text-xl font-bold leading-tight ${c.value === null ? 'text-[#94a3b8]' : textColor(c.value)}`}>
                {c.value === null ? '—' : `${c.value}%`}
              </div>
              <div className="text-[10px] text-[#94a3b8] truncate">{c.note}</div>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-[#64748b] xl:text-right shrink-0 leading-relaxed">
          <div>
            <strong className="text-[#1e293b]">{formatQty(plant.produced)}</strong> good ·{' '}
            <strong className="text-rose-600">{formatQty(plant.rejected)}</strong> rejected
          </div>
          <div>
            <strong className="text-[#1e293b]">{plant.downtime}</strong> min downtime · {plant.measured}/{rows.length} machines measured
          </div>
        </div>
      </div>

      {plannedShiftCount === 0 && rows.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
          No shift is scheduled for {shortDay(selectedDate)}
          {selectedShift !== 'all' ? ' in the selected shift' : ''}, so there is no planned time to measure against. Check the
          working days in the Shift master.
        </div>
      )}

      {/* Machine table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-330px)] min-h-[200px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 text-[#64748b] text-[11px] z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Machine</th>
                <th className="text-left px-3 py-2 font-semibold">OEE</th>
                <th className="text-left px-3 py-2 font-semibold">Availability</th>
                <th className="text-left px-3 py-2 font-semibold">Performance</th>
                <th className="text-left px-3 py-2 font-semibold">Quality</th>
                <th className="text-right px-3 py-2 font-semibold">Good</th>
                <th className="text-right px-3 py-2 font-semibold">Rejected</th>
                <th className="text-right px-3 py-2 font-semibold">Downtime</th>
                <th className="text-right px-3 py-2 font-semibold">Run / Planned</th>
                <th className="text-left px-3 py-2 font-semibold">Last 7 days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-[#94a3b8]">
                    No machines found for this warehouse
                  </td>
                </tr>
              )}
              {rows.map(({ machine, workCenterName, oee, trend }) => (
                <tr key={machine.id} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${statusDot(machine.status)}`} title={machine.status} />
                      <span className="font-semibold text-[#1e293b]">{machine.name}</span>
                    </div>
                    <div className="text-[11px] text-[#94a3b8] pl-3.5">{workCenterLookup.labelOf(null, workCenterName)}</div>
                  </td>
                  <td className="px-3 py-2">
                    {oee.hasData ? (
                      <span className={`text-sm font-bold ${textColor(oee.asPercent.oee)}`}>{oee.asPercent.oee}%</span>
                    ) : (
                      <span className="text-[#94a3b8]" title="No output or downtime recorded">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Metric pct={oee.asPercent.availability} muted={oee.plannedMinutes === 0} />
                  </td>
                  <td className="px-3 py-2">
                    {oee.hasCapacity ? (
                      <Metric pct={oee.asPercent.performance} muted={!oee.hasData} />
                    ) : (
                      <span className="text-[10px] text-amber-700" title="Set capacity per hour on the machine">
                        Capacity not set
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Metric pct={oee.asPercent.quality} muted={oee.producedQty + oee.rejectedQty === 0} />
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-[#1e293b]">{formatQty(oee.producedQty)}</td>
                  <td className={`px-3 py-2 text-right ${oee.rejectedQty > 0 ? 'text-rose-600 font-semibold' : 'text-[#94a3b8]'}`}>
                    {formatQty(oee.rejectedQty)}
                  </td>
                  <td className={`px-3 py-2 text-right ${oee.downtimeMinutes > 0 ? 'text-[#1e293b] font-semibold' : 'text-[#94a3b8]'}`}>
                    {oee.downtimeMinutes} min
                  </td>
                  <td className="px-3 py-2 text-right text-[#64748b] whitespace-nowrap">
                    {oee.runTime} / {oee.plannedMinutes} min
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-end gap-0.5 h-7 w-24">
                      {trend.map((t, idx) => (
                        <div
                          key={last7Days[idx]}
                          className={`flex-1 rounded-sm ${t.hasData ? barColor(t.asPercent.oee) : 'bg-slate-200'}`}
                          style={{ height: `${t.hasData ? Math.max(8, t.asPercent.oee) : 8}%` }}
                          title={`${shortDay(last7Days[idx])}: ${t.hasData ? `${t.asPercent.oee}%` : 'no data'}`}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
